import type { Editor } from "@tiptap/core";
import { getActiveEditor } from "./activeEditor";
import { matchesEvent } from "./keys";
import { getBindings } from "./registry";
import { isTypingInPlainField, resolveScopeChain } from "./scope";
import type { Scope, ShortcutContext } from "./types";

/**
 * Shared guards, applied once here so no binding has to repeat them.
 * Returns true when a binding claimed the keystroke.
 */
function tryScope(scope: Scope, event: KeyboardEvent, editor: Editor | null): boolean {
  const inPlainField = isTypingInPlainField(event, editor);

  for (const binding of getBindings(scope)) {
    if (!matchesEvent(event, binding.parsed)) continue;
    if (inPlainField && !binding.allowInInput) continue;
    if (binding.requiresEditable && !editor?.isEditable) continue;

    const ctx: ShortcutContext = { editor, scope, event };
    if (binding.when && !binding.when(ctx)) continue;

    // An explicit `false` means "not mine after all" — keep walking the chain
    // and leave the browser default alone.
    if (binding.run(ctx) === false) continue;
    return true;
  }
  return false;
}

/**
 * Window-level dispatch. Owns modifier combinations and Escape; bare editing
 * keys are delivered through the ProseMirror bridge instead, because
 * ProseMirror binds to the editor DOM node and consumes them before a
 * bubble-phase window listener runs.
 */
export function dispatchKeyEvent(event: KeyboardEvent): void {
  // Mid-composition keystrokes belong to the IME, not to us.
  if (event.isComposing || event.keyCode === 229) return;
  if (event.defaultPrevented) return;

  const editor = getActiveEditor();

  for (const scope of resolveScopeChain(editor)) {
    if (tryScope(scope, event, editor)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }
}
